import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { LayoutGrid, ShoppingBag, User, LifeBuoy, LogOut, Archive } from "lucide-react";

const NAV = [
  { to: "/espace", end: true, key: "cnav.orders", icon: LayoutGrid },
  { to: "/espace/commander", key: "cnav.order", icon: ShoppingBag },
  { to: "/espace/compte", key: "cnav.account", icon: User },
  { to: "/espace/archives", key: "nav.archives", icon: Archive },
  { to: "/espace/contact", key: "cnav.help", icon: LifeBuoy },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/contact/unread").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    window.addEventListener("contact-updated", load);
    const tmr = setInterval(load, 15000);
    return () => { clearInterval(tmr); window.removeEventListener("contact-updated", load); };
  }, []);

  const doLogout = async () => { await logout(); toast.success(t("common.logout")); navigate("/login"); };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Logo size="sm" />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-neutral-500 sm:inline">{user?.name}</span>
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" data-testid="client-logout-button" onClick={doLogout} className="gap-2 rounded-sm text-neutral-600 hover:text-black">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("common.logout")}</span>
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 pb-2">
          {NAV.map(({ to, end, key, icon: Icon }) => (
            <NavLink key={to} to={to} end={end}
              data-testid={`client-nav-${to.split("/").pop() || "home"}`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "border-black text-black" : "border-transparent text-neutral-400 hover:text-black"
                }`
              }>
              <Icon className="h-4 w-4" strokeWidth={1.75} /> {t(key)}
              {key === "cnav.help" && unread > 0 && (
                <span data-testid="client-contact-badge" className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}
